import { BaseApi } from "../../utils/BaseApi";
import { HttpMethodEnum } from "../models/HttpMethodEnum";
import {
  CreateInvoiceBillingReminderParams,
  CreateInvoiceQuoteParams,
  CreateInvoiceSaleParams,
  InvoiceBillingReminderFilters,
  InvoiceBillingReminderModel,
  InvoiceBillingReminderRenewResult,
  InvoiceBillingReminderSummaryModel,
  InvoiceCatalogItemModel,
  InvoiceCatalogPayload,
  InvoiceCategoryModel,
  InvoiceCustomerModel,
  InvoiceLyproCompanyModel,
  InvoicePaymentModel,
  InvoiceQuoteModel,
  InvoiceQuotePreviewResult,
  InvoiceSaleModel,
  InvoiceSalesFilters,
  InvoiceSalesSummaryModel,
  InvoiceSellerModel,
  InvoiceUsageQuoteModel,
  UpdateInvoiceBillingReminderParams,
} from "../models/Invoices/InvoiceModels";

class InvoiceApi extends BaseApi {
  private static instance: InvoiceApi;

  public static get(): InvoiceApi {
    if (!this.instance) {
      this.instance = new InvoiceApi();
    }
    return this.instance;
  }

  public catalog = BaseApi.createEndpoint<InvoiceCatalogPayload, null>(
    HttpMethodEnum.GET,
    "/invoices/catalog"
  );

  public lyproCompanies = BaseApi.createEndpoint<
    InvoiceLyproCompanyModel[],
    null
  >(HttpMethodEnum.GET, "/invoices/lypro-companies");

  public usageQuote = BaseApi.createEndpoint<
    InvoiceUsageQuoteModel,
    { customerId: string; itemId: string; month?: string } | null
  >(HttpMethodEnum.GET, "/invoices/usage-quote");

  public quotes = BaseApi.createEndpoint<InvoiceQuoteModel[], null>(
    HttpMethodEnum.GET,
    "/invoices/quotes"
  );

  public previewQuote = BaseApi.createEndpoint<
    InvoiceQuotePreviewResult,
    CreateInvoiceQuoteParams
  >(HttpMethodEnum.POST, "/invoices/quotes/preview");

  public createQuote = BaseApi.createEndpoint<
    InvoiceQuoteModel,
    CreateInvoiceQuoteParams
  >(HttpMethodEnum.POST, "/invoices/quotes");

  public categories = BaseApi.createEndpoint<InvoiceCategoryModel[], null>(
    HttpMethodEnum.GET,
    "/invoices/categories"
  );

  public createCategory = BaseApi.createEndpoint<
    InvoiceCategoryModel,
    Partial<InvoiceCategoryModel>
  >(HttpMethodEnum.POST, "/invoices/categories");

  public updateCategory = (id: string) =>
    BaseApi.createEndpoint<InvoiceCategoryModel, Partial<InvoiceCategoryModel>>(
      HttpMethodEnum.PUT,
      `/invoices/categories/${id}`
    );

  public deleteCategory = (id: string) =>
    BaseApi.createEndpoint<{ deleted: boolean }, null>(
      HttpMethodEnum.DELETE,
      `/invoices/categories/${id}`
    );

  public items = BaseApi.createEndpoint<InvoiceCatalogItemModel[], null>(
    HttpMethodEnum.GET,
    "/invoices/items"
  );

  public createItem = BaseApi.createEndpoint<
    InvoiceCatalogItemModel,
    Partial<InvoiceCatalogItemModel>
  >(HttpMethodEnum.POST, "/invoices/items");

  public updateItem = (id: string) =>
    BaseApi.createEndpoint<
      InvoiceCatalogItemModel,
      Partial<InvoiceCatalogItemModel>
    >(HttpMethodEnum.PUT, `/invoices/items/${id}`);

  public deleteItem = (id: string) =>
    BaseApi.createEndpoint<{ deleted: boolean }, null>(
      HttpMethodEnum.DELETE,
      `/invoices/items/${id}`
    );

  public customers = BaseApi.createEndpoint<InvoiceCustomerModel[], null>(
    HttpMethodEnum.GET,
    "/invoices/customers"
  );

  public createCustomer = BaseApi.createEndpoint<
    InvoiceCustomerModel,
    Partial<InvoiceCustomerModel>
  >(HttpMethodEnum.POST, "/invoices/customers");

  public updateCustomer = (id: string) =>
    BaseApi.createEndpoint<InvoiceCustomerModel, Partial<InvoiceCustomerModel>>(
      HttpMethodEnum.PUT,
      `/invoices/customers/${id}`
    );

  public deleteCustomer = (id: string) =>
    BaseApi.createEndpoint<{ deleted: boolean }, null>(
      HttpMethodEnum.DELETE,
      `/invoices/customers/${id}`
    );

  public sellers = BaseApi.createEndpoint<InvoiceSellerModel[], null>(
    HttpMethodEnum.GET,
    "/invoices/sellers"
  );

  public createSeller = BaseApi.createEndpoint<
    InvoiceSellerModel,
    Partial<InvoiceSellerModel>
  >(HttpMethodEnum.POST, "/invoices/sellers");

  public updateSeller = (id: string) =>
    BaseApi.createEndpoint<InvoiceSellerModel, Partial<InvoiceSellerModel>>(
      HttpMethodEnum.PUT,
      `/invoices/sellers/${id}`
    );

  public deleteSeller = (id: string) =>
    BaseApi.createEndpoint<{ deleted: boolean }, null>(
      HttpMethodEnum.DELETE,
      `/invoices/sellers/${id}`
    );

  public billingReminders = BaseApi.createEndpoint<
    InvoiceBillingReminderModel[],
    InvoiceBillingReminderFilters | null
  >(HttpMethodEnum.GET, "/invoices/billing-reminders");

  public billingReminderSummary = BaseApi.createEndpoint<
    InvoiceBillingReminderSummaryModel,
    InvoiceBillingReminderFilters | null
  >(HttpMethodEnum.GET, "/invoices/billing-reminders/summary");

  public createBillingReminder = BaseApi.createEndpoint<
    InvoiceBillingReminderModel,
    CreateInvoiceBillingReminderParams
  >(HttpMethodEnum.POST, "/invoices/billing-reminders");

  public updateBillingReminder = (id: string) =>
    BaseApi.createEndpoint<
      InvoiceBillingReminderModel,
      UpdateInvoiceBillingReminderParams
    >(HttpMethodEnum.PUT, `/invoices/billing-reminders/${id}`);

  public renewBillingReminder = (id: string) =>
    BaseApi.createEndpoint<
      InvoiceBillingReminderRenewResult,
      CreateInvoiceSaleParams | null
    >(
      HttpMethodEnum.POST,
      `/invoices/billing-reminders/${id}/renew`
    );

  public cancelBillingReminder = (id: string) =>
    BaseApi.createEndpoint<InvoiceBillingReminderModel, { reason?: string }>(
      HttpMethodEnum.POST,
      `/invoices/billing-reminders/${id}/cancel`
    );

  public deleteBillingReminder = (id: string) =>
    BaseApi.createEndpoint<{ deleted: boolean }, null>(
      HttpMethodEnum.DELETE,
      `/invoices/billing-reminders/${id}`
    );

  public sales = BaseApi.createEndpoint<InvoiceSaleModel[], InvoiceSalesFilters | null>(
    HttpMethodEnum.GET,
    "/invoices/sales"
  );

  public salesSummary = BaseApi.createEndpoint<
    InvoiceSalesSummaryModel,
    InvoiceSalesFilters | null
  >(HttpMethodEnum.GET, "/invoices/sales/summary");

  public sale = (id: string) =>
    BaseApi.createEndpoint<InvoiceSaleModel, null>(
      HttpMethodEnum.GET,
      `/invoices/sales/${id}`
    );

  public createSale = BaseApi.createEndpoint<
    InvoiceSaleModel,
    CreateInvoiceSaleParams
  >(HttpMethodEnum.POST, "/invoices/sales");

  public regenerateSalePdfs = (id: string) =>
    BaseApi.createEndpoint<InvoiceSaleModel, null>(
      HttpMethodEnum.POST,
      `/invoices/sales/${id}/pdfs`
    );

  public addPayment = (id: string) =>
    BaseApi.createEndpoint<InvoiceSaleModel, Partial<InvoicePaymentModel>>(
      HttpMethodEnum.POST,
      `/invoices/sales/${id}/payments`
    );

  public deletePayment = (saleId: string, paymentId: string) =>
    BaseApi.createEndpoint<InvoiceSaleModel, null>(
      HttpMethodEnum.DELETE,
      `/invoices/sales/${saleId}/payments/${paymentId}`
    );

  public voidSale = (id: string) =>
    BaseApi.createEndpoint<InvoiceSaleModel, null>(
      HttpMethodEnum.POST,
      `/invoices/sales/${id}/void`
    );
}

export const invoiceApi = InvoiceApi.get();
